/**
 * Mock comments data for development.
 * Distributed across various posts with realistic fitness community language.
 */
import { subHours, subDays, subMinutes } from 'date-fns';
import { MOCK_USERS, MOCK_CURRENT_USER_ID } from './mock-users.data';

const now = new Date();
const u = (id) => MOCK_USERS.find((x) => x.id === id) || MOCK_USERS[0];

const makeComment = (overrides) => {
  const author = u(overrides.authorId);
  return {
    id: overrides.id,
    text: '',
    authorId: '',
    createdAt: new Date().toISOString(),
    updatedAt: null,
    _docSnapshot: null,
    ...overrides,
    authorName: author.displayName,
    authorUsername: author.username,
    authorAvatarUrl: author.avatarUrl,
  };
};

/**
 * Comments keyed by post ID for easy lookup.
 */
export const MOCK_COMMENTS_BY_POST = {
  // Deadlift PR text post
  mock_post_t01: [
    makeComment({ id: 'mock_cmt_01', text: 'That\'s incredible! What program are you following?', authorId: 'mock_user_002', createdAt: subMinutes(now, 90).toISOString() }),
    makeComment({ id: 'mock_cmt_02', text: '405 is elite! Next stop 4 plates per side!', authorId: 'mock_user_003', createdAt: subMinutes(now, 80).toISOString() }),
    makeComment({ id: 'mock_cmt_03', text: 'Keep grinding! PRs are made in the gym but earned at home with recovery.', authorId: 'mock_user_005', createdAt: subMinutes(now, 60).toISOString() }),
    makeComment({ id: 'mock_cmt_04', text: 'Sumo or conventional? I\'ve been stuck at 365 for weeks.', authorId: 'mock_user_007', createdAt: subMinutes(now, 45).toISOString() }),
    makeComment({ id: 'mock_cmt_05', text: 'Conventional! Have you tried deficit pulls? They helped me break through my plateau.', authorId: MOCK_CURRENT_USER_ID, createdAt: subMinutes(now, 30).toISOString() }),
  ],
  // Morning run text post
  mock_post_t02: [
    makeComment({ id: 'mock_cmt_06', text: '5am crew checking in! Rain or shine.', authorId: 'mock_user_009', createdAt: subHours(now, 5).toISOString() }),
    makeComment({ id: 'mock_cmt_07', text: 'Running in the rain is the best! No crowds, just you and the road.', authorId: 'mock_user_010', createdAt: subHours(now, 4).toISOString() }),
    makeComment({ id: 'mock_cmt_08', text: 'What pace were you doing? I\'m trying to build up to 6 miles.', authorId: 'mock_user_004', createdAt: subHours(now, 3).toISOString() }),
  ],
  // Pre-workout meal question
  mock_post_t04: [
    makeComment({ id: 'mock_cmt_09', text: 'Same here! Oatmeal and banana never fails.', authorId: 'mock_user_008', createdAt: subDays(now, 2).toISOString() }),
    makeComment({ id: 'mock_cmt_10', text: 'I usually go with rice cakes and honey. Quick digesting carbs.', authorId: 'mock_user_005', createdAt: subDays(now, 2).toISOString() }),
    makeComment({ id: 'mock_cmt_11', text: 'Black coffee and a banana 30 min out. Simple and effective.', authorId: 'mock_user_003', createdAt: subDays(now, 2).toISOString() }),
    makeComment({ id: 'mock_cmt_12', text: 'I train fasted in the morning. Once you adapt it\'s great.', authorId: 'mock_user_009', createdAt: subDays(now, 2).toISOString() }),
  ],
  // Push day workout post
  mock_post_w01: [
    makeComment({ id: 'mock_cmt_13', text: 'Great session! How long do you rest between bench sets?', authorId: 'mock_user_007', createdAt: subHours(now, 3).toISOString() }),
    makeComment({ id: 'mock_cmt_14', text: 'About 2-3 minutes for the heavy sets. Shorter rest for accessories.', authorId: MOCK_CURRENT_USER_ID, createdAt: subHours(now, 2).toISOString() }),
  ],
  // Leg day workout post
  mock_post_w02: [
    makeComment({ id: 'mock_cmt_15', text: 'Those squat numbers are insane! Respect.', authorId: 'mock_user_006', createdAt: subHours(now, 7).toISOString() }),
    makeComment({ id: 'mock_cmt_16', text: 'How deep are you going on the squats? ATG?', authorId: MOCK_CURRENT_USER_ID, createdAt: subHours(now, 6).toISOString() }),
    makeComment({ id: 'mock_cmt_17', text: 'Always below parallel. Mobility work is key!', authorId: 'mock_user_003', createdAt: subHours(now, 5).toISOString() }),
  ],
  // Meal prep post
  mock_post_m02: [
    makeComment({ id: 'mock_cmt_18', text: 'What\'s your macro split looking like for the week?', authorId: 'mock_user_004', createdAt: subDays(now, 1).toISOString() }),
    makeComment({ id: 'mock_cmt_19', text: 'Meal prep is a game changer. How long does this last in the fridge?', authorId: 'mock_user_008', createdAt: subDays(now, 1).toISOString() }),
    makeComment({ id: 'mock_cmt_20', text: 'About 4-5 days. I usually add different sauces to keep it interesting.', authorId: 'mock_user_005', createdAt: subDays(now, 1).toISOString() }),
  ],
  // 3-month progress post
  mock_post_p01: [
    makeComment({ id: 'mock_cmt_21', text: 'Amazing transformation! You\'re inspiring the whole community.', authorId: 'mock_user_006', createdAt: subDays(now, 1).toISOString() }),
    makeComment({ id: 'mock_cmt_22', text: 'The difference is night and day. What was your diet like?', authorId: 'mock_user_003', createdAt: subDays(now, 1).toISOString() }),
    makeComment({ id: 'mock_cmt_23', text: 'Mostly whole foods, high protein around 160g daily. No crazy restriction, just consistent tracking.', authorId: MOCK_CURRENT_USER_ID, createdAt: subDays(now, 1).toISOString() }),
    makeComment({ id: 'mock_cmt_24', text: 'This is exactly the motivation I needed today. Thank you for sharing!', authorId: 'mock_user_008', createdAt: subHours(now, 22).toISOString() }),
    makeComment({ id: 'mock_cmt_25', text: 'The face gains are always the best part of a cut. Looking great!', authorId: 'mock_user_010', createdAt: subHours(now, 20).toISOString() }),
  ],
  // Salmon dinner meal post
  mock_post_m04: [
    makeComment({ id: 'mock_cmt_26', text: 'Salmon and sweet potato is the perfect combo!', authorId: 'mock_user_004', createdAt: subDays(now, 3).toISOString() }),
    makeComment({ id: 'mock_cmt_27', text: 'Do you bake or pan sear the salmon?', authorId: MOCK_CURRENT_USER_ID, createdAt: subDays(now, 3).toISOString() }),
    makeComment({ id: 'mock_cmt_28', text: 'Pan sear skin-side down for 4 minutes then flip and finish in the oven at 400F for 6 minutes. Perfection!', authorId: 'mock_user_002', createdAt: subDays(now, 3).toISOString() }),
  ],
  // Calisthenics progress post
  mock_post_p03: [
    makeComment({ id: 'mock_cmt_29', text: 'From 0 to 20 pull-ups is insane progress. What was your weekly programming?', authorId: 'mock_user_003', createdAt: subDays(now, 8).toISOString() }),
    makeComment({ id: 'mock_cmt_30', text: 'The body recomp is crazy. You lost weight but look way more muscular.', authorId: 'mock_user_005', createdAt: subDays(now, 8).toISOString() }),
  ],
};

/**
 * Flat list of all mock comments.
 */
export const MOCK_ALL_COMMENTS = Object.values(MOCK_COMMENTS_BY_POST).flat();
